import { blueprintBg } from "@/theme/styleHelpers";
import { ConfirmDialog } from "@components/ConfirmDialog";
/**
 * CircuitsTab — the project detail surface that finally exposes the
 * multi-Circuit lifecycle (IR plan §8.3 + Phase 14). Backend ships the
 * keepers + endpoints; this is where the user discovers them.
 *
 * Layout:
 *   * Status filter chip group at the top (reuses CircuitStatusFilter).
 *   * Grid of CircuitCards grouped by kind (main first, then candidates,
 *     fixtures, scratches). Reads as a small constellation of related
 *     drawings, not a flat list.
 *   * Each card has lifecycle actions wired to the real endpoints
 *     (branch / promote / release / archive / restore).
 *
 * Empty state — branded "READY FOR INPUT" plate so the page never looks
 * unfinished.
 */
import { BranchVersionDialog } from "@features/projects/components/BranchVersionDialog";
import {
  CircuitStatusFilter,
  type CircuitStatusFilterValue,
} from "@features/projects/components/CircuitStatusFilter";
import { UploadVersionDialog } from "@features/versions/components/UploadVersionDialog";
import type { CircuitDTO, CircuitStatus } from "@models/circuit-ir";
import ChatIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  useArchiveCircuit,
  useCircuits,
  usePatchCircuit,
  usePromoteCircuit,
} from "../hooks/useCircuitLifecycle";
import { CircuitBranchGraph } from "./CircuitBranchGraph";
import { CircuitCard } from "./CircuitCard";
import { MappingBench } from "./MappingBench";

interface CircuitsTabProps {
  projectId: string;
  /** URL-route project name, used by UploadVersionDialog. Falls back
   *  to projectId if the parent didn't thread it through. */
  projectName?: string;
  /** Owning org id for the project, null = personal. */
  organisationId?: string | null;
}

const KIND_GROUP_LABEL: Record<string, string> = {
  main: "Main board",
  candidate: "Candidates",
  fixture: "Fixtures",
  scratch: "Scratch",
};

export function CircuitsTab({
  projectId,
  projectName,
  organisationId = null,
}: CircuitsTabProps) {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CircuitStatusFilterValue>(
    () => new Set(["active", "draft"]),
  );
  const filterArray = useMemo<CircuitStatus[]>(() => {
    const out: CircuitStatus[] = [];
    if (statusFilter.has("active")) {
      out.push("active");
    }
    if (statusFilter.has("draft")) {
      out.push("draft");
      out.push("ephemeral");
    }
    if (statusFilter.has("released")) out.push("released");
    if (statusFilter.has("archived")) out.push("archived");
    return out;
  }, [statusFilter]);

  const {
    data: circuits,
    isLoading,
    error,
  } = useCircuits(projectId, filterArray);

  // Branch is fired from inside BranchVersionDialog (which uses
  // useBranchCircuit directly); kept out of the tab-level scope to
  // avoid double-firing.
  const promote = usePromoteCircuit();
  const archive = useArchiveCircuit();
  const patch = usePatchCircuit();

  const [branchTarget, setBranchTarget] = useState<CircuitDTO | null>(null);
  const [mergeSource, setMergeSource] = useState<CircuitDTO | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CircuitDTO | null>(null);

  // Group by kind — preserves the "main · candidates · fixtures · scratch"
  // reading order regardless of API sort.
  const grouped = useMemo(() => {
    const groups: Record<string, CircuitDTO[]> = {};
    for (const c of circuits ?? []) {
      if (!groups[c.kind]) groups[c.kind] = [];
      groups[c.kind].push(c);
    }
    return groups;
  }, [circuits]);

  // Default merge target — the project's main board. Multi-main projects
  // fall through to whichever main the API returned first.
  const mainCircuit = useMemo(
    () => (circuits ?? []).find((c) => c.kind === "main") ?? null,
    [circuits],
  );

  const totalCounts = useMemo(() => {
    const counts: Partial<
      Record<"active" | "draft" | "released" | "archived", number>
    > = {};
    for (const c of circuits ?? []) {
      const bucket =
        c.status === "draft" || c.status === "ephemeral"
          ? "draft"
          : c.status === "released"
            ? "released"
            : c.status === "archived"
              ? "archived"
              : "active";
      counts[bucket] = (counts[bucket] ?? 0) + 1;
    }
    return counts;
  }, [circuits]);

  if (isLoading && !circuits) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Could not load circuits: {(error as Error).message}
      </Alert>
    );
  }

  const hasAny = (circuits?.length ?? 0) > 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, height: "100%", overflowY: "auto" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "text.disabled",
            fontSize: 11,
            letterSpacing: "0.14em",
          }}
        >
          {circuits?.length ?? 0} CIRCUIT{circuits?.length === 1 ? "" : "S"}
        </Typography>
        <CircuitStatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
          counts={totalCounts}
        />
      </Stack>

      {!hasAny && (
        <CircuitsEmptyState
          onUpload={() => setUploadOpen(true)}
          onChat={() =>
            void navigate({
              to: "/projects/$projectId/chat",
              params: { projectId },
            })
          }
        />
      )}

      {hasAny && (circuits?.length ?? 0) >= 2 && (
        <CircuitBranchGraph circuits={circuits ?? []} />
      )}

      {hasAny && (
        <Stack spacing={3}>
          {(["main", "candidate", "fixture", "scratch"] as const).map(
            (kind) => {
              const items = grouped[kind] ?? [];
              if (items.length === 0) return null;
              return (
                <Box key={kind}>
                  <Typography
                    variant="overline"
                    sx={(t) => ({
                      color: t.palette.editorial.main,
                      fontSize: 10,
                      letterSpacing: "0.16em",
                      mb: 1,
                      display: "block",
                    })}
                  >
                    {KIND_GROUP_LABEL[kind]} · {items.length}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        lg: "repeat(3, 1fr)",
                      },
                      gap: 1.5,
                    }}
                  >
                    {items.map((c) => (
                      <Box
                        key={c.id}
                        id={`circuit-${c.id}`}
                        sx={(t) => ({
                          // Hook for CircuitBranchGraph's scroll-then-flash.
                          // Brief amber halo so the user sees where the
                          // graph-click landed; auto-clears via timeout.
                          "&.circuit-card-flash": {
                            boxShadow: `0 0 0 3px ${t.palette.editorial.main}`,
                            borderRadius: 2,
                            transition: `box-shadow ${t.motion.slow}`,
                          },
                        })}
                      >
                        <CircuitCard
                          circuit={c}
                          onBranch={() => setBranchTarget(c)}
                          onPromote={() =>
                            promote.mutate({
                              circuitId: c.id,
                              payload: { publish: true },
                            })
                          }
                          onArchive={() => setArchiveTarget(c)}
                          onRestore={() =>
                            patch.mutate({
                              circuitId: c.id,
                              payload: { status: "active" },
                            })
                          }
                          onMerge={
                            mainCircuit && c.kind === "candidate"
                              ? () => setMergeSource(c)
                              : undefined
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            },
          )}
        </Stack>
      )}

      <BranchVersionDialog
        open={!!branchTarget}
        projectName={branchTarget?.name ?? ""}
        sourceVersionLabel={
          branchTarget?.current_version_semver ||
          (branchTarget?.current_version_number != null
            ? `v${branchTarget.current_version_number}`
            : "current")
        }
        sourceCircuitId={branchTarget?.id}
        onClose={() => setBranchTarget(null)}
        onBranched={() => setBranchTarget(null)}
      />

      <MappingBench
        open={!!mergeSource}
        sourceCircuit={mergeSource}
        targetCircuit={mainCircuit}
        onClose={() => setMergeSource(null)}
      />

      <UploadVersionDialog
        open={uploadOpen}
        projectName={projectName ?? projectId}
        organisationId={organisationId}
        onClose={() => setUploadOpen(false)}
      />

      <ConfirmDialog
        open={!!archiveTarget}
        title="Archive this circuit?"
        message={
          archiveTarget
            ? `"${archiveTarget.name}" will be hidden from the active list. You can restore it later from the Archived filter.`
            : ""
        }
        confirmLabel="Archive"
        dangerous
        onConfirm={() => {
          if (archiveTarget) {
            archive.mutate({ circuitId: archiveTarget.id, projectId });
          }
          setArchiveTarget(null);
        }}
        onCancel={() => setArchiveTarget(null)}
      />
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────

const phosphorPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 1; }
`;

const plateEnter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

interface CornerBracket {
  id: "tl" | "tr" | "bl" | "br";
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  bt: number;
  bb: number;
  bl: number;
  br: number;
}

const CORNER_BRACKETS: readonly CornerBracket[] = [
  { id: "tl", top: 10, left: 10, bt: 1, bl: 1, bb: 0, br: 0 },
  { id: "tr", top: 10, right: 10, bt: 1, br: 1, bb: 0, bl: 0 },
  { id: "bl", bottom: 10, left: 10, bb: 1, bl: 1, bt: 0, br: 0 },
  { id: "br", bottom: 10, right: 10, bb: 1, br: 1, bt: 0, bl: 0 },
];

interface EmptyStateProps {
  onUpload: () => void;
  onChat: () => void;
}

/** Drafting-plate empty state. Title block top-left, constellation as
 *  a corner stamp, two real CTAs, and a numbered legend that teaches
 *  the multi-circuit lifecycle. Same visual language as the populated
 *  CircuitCard grid below — blueprint grid, phosphor accent, editorial
 *  amber, JetBrains Mono — so the empty page reads as "prepped bench"
 *  rather than "missing content". */
function CircuitsEmptyState({ onUpload, onChat }: EmptyStateProps) {
  return (
    <Box
      sx={(t) => ({
        position: "relative",
        ...blueprintBg(t),
        borderRadius: 2,
        border: `1px solid ${t.palette.border.default}`,
        py: { xs: 3.5, sm: 4.5 },
        px: { xs: 2.75, sm: 4.5 },
        overflow: "hidden",
        animation: `${plateEnter} 320ms cubic-bezier(0.4, 0, 0.2, 1) both`,
        // Soft radial vignette so the grid has depth instead of reading flat.
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at 80% 0%, transparent 0%, ${alpha(
            t.palette.surface.sunken,
            0.5,
          )} 75%)`,
          pointerEvents: "none",
        },
      })}
    >
      {/* Drafting corner brackets — hairline, four corners */}
      {CORNER_BRACKETS.map((pos) => (
        <Box
          key={pos.id}
          sx={(t) => ({
            position: "absolute",
            width: 14,
            height: 14,
            borderColor: alpha(t.palette.accent.main, 0.45),
            borderStyle: "solid",
            borderTopWidth: pos.bt,
            borderBottomWidth: pos.bb,
            borderLeftWidth: pos.bl,
            borderRightWidth: pos.br,
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            right: pos.right,
            zIndex: 2,
          })}
        />
      ))}

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* TITLE BLOCK + corner constellation */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Box
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 10,
              letterSpacing: "0.18em",
              lineHeight: 1.5,
            })}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.85}
              sx={{ mb: 0.85 }}
            >
              <Box
                sx={(t) => ({
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: t.palette.accent.main,
                  boxShadow: `0 0 8px ${alpha(t.palette.accent.main, 0.65)}`,
                  animation: `${phosphorPulse} 2400ms ease-in-out infinite`,
                })}
              />
              <Box
                sx={(t) => ({
                  color: t.palette.editorial.main,
                  fontWeight: 700,
                })}
              >
                CIRCUIT BAY
              </Box>
            </Stack>
            <Box
              sx={(t) => ({
                height: 1,
                width: 168,
                background: `linear-gradient(90deg, ${alpha(
                  t.palette.editorial.main,
                  0.55,
                )}, transparent)`,
                mb: 1.25,
              })}
            />
            <Box sx={{ color: "text.disabled" }}>
              STATUS{" "}
              <Box
                component="span"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                · AWAITING_INGEST
              </Box>
            </Box>
          </Box>

          {/* Constellation as corner mark — was 80×80 centerpiece, now 52px
              top-right stamp. Same motif, supports the headline instead of
              competing with it. */}
          <Box
            component="svg"
            viewBox="0 0 80 80"
            width={52}
            height={52}
            fill="none"
            sx={(t) => ({
              stroke: t.palette.accent.main,
              strokeWidth: 1.5,
              strokeLinecap: "round",
              strokeLinejoin: "round",
              opacity: 0.6,
              filter: `drop-shadow(0 0 6px ${alpha(t.palette.accent.main, 0.4)})`,
              flexShrink: 0,
            })}
          >
            <circle cx="20" cy="22" r="5" />
            <circle cx="60" cy="22" r="5" />
            <circle cx="40" cy="58" r="5" />
            <path d="M 25 22 L 55 22" />
            <path d="M 22 28 L 36 54" />
            <path d="M 58 28 L 44 54" />
          </Box>
        </Stack>

        {/* HEADLINE */}
        <Box sx={{ mt: { xs: 3, sm: 3.5 } }}>
          <Typography
            variant="h5"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              fontSize: { xs: "1.2rem", sm: "1.35rem" },
            }}
          >
            Lay down the first trace.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "text.secondary",
              maxWidth: 480,
              lineHeight: 1.55,
            }}
          >
            A circuit holds one design — its components, nets, and the IR that
            grows from them. Seed a main board from a netlist, then branch
            candidates from chat.
          </Typography>
        </Box>

        {/* CTAs */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          sx={{ mt: 2.5 }}
        >
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon fontSize="small" />}
            onClick={onUpload}
            sx={{ fontWeight: 600 }}
          >
            Seed main board
          </Button>
          <Button
            variant="outlined"
            startIcon={<ChatIcon fontSize="small" />}
            onClick={onChat}
          >
            Open chat
          </Button>
        </Stack>

        {/* DRAFTING LEGEND — teaches the multi-circuit model in two lines */}
        <Box sx={{ mt: { xs: 3.5, sm: 4 } }}>
          <Box
            sx={(t) => ({
              height: 1,
              background: `linear-gradient(90deg, ${alpha(
                t.palette.border.strong,
                0.7,
              )}, transparent)`,
              mb: 1.75,
            })}
          />
          <Stack spacing={1}>
            <LegendStep
              n="01"
              title="upload netlist"
              hint="KiCad · EasyEDA · Altium · SPICE"
            />
            <LegendStep
              n="02"
              title="branch from chat"
              hint="candidates appear beside the main board"
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

function LegendStep({
  n,
  title,
  hint,
}: {
  n: string;
  title: string;
  hint: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={1.25}
      sx={{ flexWrap: "wrap", rowGap: 0.25 }}
    >
      <Box
        sx={(t) => ({
          fontFamily: t.fonts.mono,
          fontSize: 11,
          color: t.palette.editorial.main,
          fontWeight: 700,
          letterSpacing: "0.1em",
        })}
      >
        {n}
      </Box>
      <Box
        sx={(t) => ({
          fontFamily: t.fonts.mono,
          fontSize: 12,
          color: t.palette.text.primary,
          letterSpacing: "0.04em",
        })}
      >
        {title}
      </Box>
      <Box sx={{ color: "text.disabled", fontSize: 11 }}>·</Box>
      <Box
        sx={{
          fontSize: 11.5,
          color: "text.disabled",
        }}
      >
        {hint}
      </Box>
    </Stack>
  );
}
